<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import subDays from 'date-fns/subDays';
import { useAlert } from 'dashboard/composables';
import ReportsAPI from 'dashboard/api/reports';
import {
  downloadCsvFile,
  generateFileName,
} from 'dashboard/helper/downloadHelper';
import { getUnixEndOfDay, getUnixStartOfDay } from 'helpers/DateHelper';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';

const { t } = useI18n();

const defaultDateRange = {
  from: getUnixStartOfDay(subDays(new Date(), 6)),
  to: getUnixEndOfDay(new Date()),
};

const DEFAULT_USAGE_BREAKDOWN = [
  {
    key: 'ios',
    label: 'iOS',
    conversationCount: 0,
    conversationPercentage: 0,
    uniqueContactsCount: 0,
    uniqueContactsPercentage: 0,
  },
  {
    key: 'android',
    label: 'Android',
    conversationCount: 0,
    conversationPercentage: 0,
    uniqueContactsCount: 0,
    uniqueContactsPercentage: 0,
  },
  {
    key: 'web',
    label: 'Web',
    conversationCount: 0,
    conversationPercentage: 0,
    uniqueContactsCount: 0,
    uniqueContactsPercentage: 0,
  },
  {
    key: 'unknown',
    label: 'Unknown',
    conversationCount: 0,
    conversationPercentage: 0,
    uniqueContactsCount: 0,
    uniqueContactsPercentage: 0,
  },
];

const buildDefaultUsageSummary = () => ({
  totalConversations: 0,
  totalUniqueContacts: 0,
  breakdown: DEFAULT_USAGE_BREAKDOWN.map(bucket => ({ ...bucket })),
});

const from = ref(defaultDateRange.from);
const to = ref(defaultDateRange.to);
const selectedInboxId = ref(null);
const selectedTeamId = ref(null);
const selectedAgentIds = ref([]);
const isDownloading = ref(false);
const usageSummary = ref(buildDefaultUsageSummary());
const isUsageSummaryLoading = ref(false);
const usageSummaryError = ref('');
let usageSummaryRequestId = 0;

const canDownload = computed(() => Boolean(from.value && to.value));
const hasUsageData = computed(() => usageSummary.value.totalConversations > 0);

const formatPercentage = value => `${Number(value || 0).toFixed(1)}%`;

const normalizeUsageSummary = payload => {
  const summary = payload?.summary || payload || {};
  const incomingBreakdown = Array.isArray(summary?.breakdown)
    ? summary.breakdown
    : [];
  const breakdownByKey = incomingBreakdown.reduce((acc, bucket) => {
    if (bucket?.key) acc[String(bucket.key)] = bucket;
    return acc;
  }, {});

  return {
    totalConversations:
      Number(
        summary?.total_conversations ?? summary?.totalConversations ?? 0
      ) || 0,
    totalUniqueContacts:
      Number(
        summary?.total_unique_contacts ?? summary?.totalUniqueContacts ?? 0
      ) || 0,
    breakdown: DEFAULT_USAGE_BREAKDOWN.map(defaultBucket => {
      const incoming = breakdownByKey[defaultBucket.key] || {};
      return {
        ...defaultBucket,
        label: incoming?.label || defaultBucket.label,
        conversationCount:
          Number(
            incoming?.conversation_count ?? incoming?.conversationCount ?? 0
          ) || 0,
        conversationPercentage:
          Number(
            incoming?.conversation_percentage ??
              incoming?.conversationPercentage ??
              0
          ) || 0,
        uniqueContactsCount:
          Number(
            incoming?.unique_contacts_count ??
              incoming?.uniqueContactsCount ??
              0
          ) || 0,
        uniqueContactsPercentage:
          Number(
            incoming?.unique_contacts_percentage ??
              incoming?.uniqueContactsPercentage ??
              0
          ) || 0,
      };
    }),
  };
};

const fetchUsageSummary = async () => {
  if (!canDownload.value) return;

  usageSummaryRequestId += 1;
  const requestId = usageSummaryRequestId;
  isUsageSummaryLoading.value = true;
  usageSummaryError.value = '';

  try {
    const response = await ReportsAPI.getLiveChatFormsUsageSummary({
      from: from.value,
      to: to.value,
      inboxId: selectedInboxId.value,
      teamId: selectedTeamId.value,
      userIds: selectedAgentIds.value,
    });

    if (requestId !== usageSummaryRequestId) return;
    usageSummary.value = normalizeUsageSummary(response?.data);
  } catch (error) {
    if (requestId !== usageSummaryRequestId) return;

    usageSummary.value = buildDefaultUsageSummary();
    usageSummaryError.value =
      error?.response?.data?.error || 'Unable to load device usage summary.';
  } finally {
    if (requestId === usageSummaryRequestId) {
      isUsageSummaryLoading.value = false;
    }
  }
};

const onFilterChange = ({
  from: nextFrom,
  to: nextTo,
  selectedInbox,
  selectedAgents,
  selectedTeam,
}) => {
  from.value = nextFrom;
  to.value = nextTo;
  selectedInboxId.value = selectedInbox?.id || null;
  selectedTeamId.value = selectedTeam?.id || null;
  selectedAgentIds.value = Array.isArray(selectedAgents)
    ? selectedAgents.map(agent => agent.id).filter(Boolean)
    : [];
  fetchUsageSummary();
};

const download = async () => {
  if (!canDownload.value) return;

  isDownloading.value = true;
  try {
    const response = await ReportsAPI.getLiveChatFormsExport({
      from: from.value,
      to: to.value,
      inboxId: selectedInboxId.value,
      teamId: selectedTeamId.value,
      userIds: selectedAgentIds.value,
    });

    const fileName = generateFileName({
      type: 'live-chat-forms',
      to: to.value,
    });
    downloadCsvFile(fileName, response.data);
    useAlert('Export ready – your download has started.');
  } catch (error) {
    useAlert(error?.response?.data?.error || t('REPORT.DATA_FETCHING_FAILED'));
  } finally {
    isDownloading.value = false;
  }
};

onMounted(() => {
  fetchUsageSummary();
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="lcf-page">
    <div class="lcf-shell">
      <div class="lcf-header">
        <div>
          <h1 class="lcf-title">Live Chat Forms Export</h1>
          <p class="lcf-subtitle">
            Download web widget chat records with marketing-friendly technology
            form fields such as device, OS, browser, user agent, source URL, and
            form submissions.
          </p>
        </div>
        <button
          class="lcf-download-btn"
          type="button"
          :disabled="!canDownload || isDownloading"
          @click="download"
        >
          <span>{{
            isDownloading ? 'Preparing export…' : 'Download CSV'
          }}</span>
        </button>
      </div>

      <div class="lcf-toolbar">
        <CompactDateInboxFilters
          :show-add-filter="false"
          show-agents
          show-teams
          livechat-only-inboxes
          @filter-change="onFilterChange"
        />
      </div>

      <div class="lcf-usage">
        <section class="lcf-card lcf-card--usage">
          <div class="lcf-usage__header">
            <h2 class="lcf-card-title">Member device usage</h2>
            <p class="lcf-usage__subtitle">Based on selected filters</p>
          </div>

          <div v-if="isUsageSummaryLoading" class="lcf-usage__state">
            Loading usage summary...
          </div>
          <div
            v-else-if="usageSummaryError"
            class="lcf-usage__state lcf-usage__state--error"
          >
            {{ usageSummaryError }}
          </div>
          <template v-else>
            <div class="lcf-usage__totals">
              <div class="lcf-usage__total">
                <span>Total conversations</span>
                <strong>{{ usageSummary.totalConversations }}</strong>
              </div>
              <div class="lcf-usage__total">
                <span>Unique contacts</span>
                <strong>{{ usageSummary.totalUniqueContacts }}</strong>
              </div>
            </div>

            <div v-if="hasUsageData" class="lcf-usage__grid">
              <article
                v-for="bucket in usageSummary.breakdown"
                :key="bucket.key"
                class="lcf-usage__item"
              >
                <h3 class="lcf-usage__item-title">{{ bucket.label }}</h3>
                <div class="lcf-usage__item-count">
                  {{ bucket.conversationCount }}
                </div>
                <p class="lcf-usage__item-meta">
                  {{ formatPercentage(bucket.conversationPercentage) }} of
                  conversations
                </p>
                <p class="lcf-usage__item-meta">
                  {{ bucket.uniqueContactsCount }} contacts ({{
                    formatPercentage(bucket.uniqueContactsPercentage)
                  }})
                </p>
              </article>
            </div>
            <p v-else class="lcf-usage__state">
              No conversations found for current filters.
            </p>
          </template>
        </section>
      </div>

      <div class="lcf-content">
        <section class="lcf-card">
          <h2 class="lcf-card-title">What gets exported</h2>
          <div class="lcf-columns">
            <ul class="lcf-list">
              <li>Chat ID and conversation status</li>
              <li>Started at and last activity at</li>
              <li>Inbox, team, and assigned agent</li>
              <li>Customer name, email, and phone</li>
              <li>Started on URL and came from URL</li>
              <li>IP address</li>
            </ul>
            <ul class="lcf-list">
              <li>OS and device name</li>
              <li>Browser and browser version</li>
              <li>Full user agent</li>
              <li>Pre-chat submitted time and responses</li>
              <li>Ticket submitted time and responses</li>
              <li>Post-chat submitted time and responses</li>
            </ul>
          </div>
        </section>

        <section class="lcf-card lcf-card--notes">
          <h2 class="lcf-card-title">Export notes</h2>
          <ul class="lcf-list">
            <li>Only web widget live chat conversations are included.</li>
            <li>
              Filters support date range, live chat inbox, agents, and team.
            </li>
            <li>
              This export is designed for marketing and ops analysis, so it
              excludes full conversation transcript content.
            </li>
            <li>
              For very large ranges, export generation may take longer than
              usual.
            </li>
          </ul>
        </section>
      </div>
    </div>
  </div>
</template>
<!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

<style scoped>
.lcf-page {
  min-height: 100vh;
  min-height: 100dvh;
  background: #fff;
}

.lcf-shell {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  overflow: visible;
}

.lcf-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 24px;
  border-bottom: 1px solid #eceff3;
}

.lcf-title {
  margin: 0;
  font-size: 22px;
  line-height: 1.2;
  font-weight: 700;
  color: #111827;
}

.lcf-subtitle {
  margin: 8px 0 0;
  max-width: 760px;
  font-size: 13px;
  line-height: 1.6;
  color: #6b7280;
}

.lcf-download-btn {
  height: 40px;
  border: none;
  border-radius: 8px;
  background: #111827;
  color: #fff;
  padding: 0 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.lcf-download-btn:hover:not(:disabled) {
  background: #1f2937;
}

.lcf-download-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.lcf-toolbar {
  position: relative;
  z-index: 10;
  padding: 12px 24px 8px;
  background: #fff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
}

.lcf-usage {
  padding: 16px 24px 0;
}

.lcf-card--usage {
  padding: 16px 20px;
}

.lcf-usage__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.lcf-usage__subtitle {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

.lcf-usage__totals {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.lcf-usage__total {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.lcf-usage__total span {
  font-size: 12px;
  color: #6b7280;
}

.lcf-usage__total strong {
  font-size: 18px;
  color: #111827;
}

.lcf-usage__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.lcf-usage__item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  background: #fff;
}

.lcf-usage__item-title {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  color: #4b5563;
}

.lcf-usage__item-count {
  margin-top: 6px;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  color: #111827;
}

.lcf-usage__item-meta {
  margin: 8px 0 0;
  font-size: 11px;
  line-height: 1.4;
  color: #6b7280;
}

.lcf-usage__state {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
}

.lcf-usage__state--error {
  color: #b91c1c;
}

.lcf-content {
  padding: 24px;
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: 20px;
}

.lcf-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  padding: 20px;
}

.lcf-card--notes {
  background: #f9fafb;
}

.lcf-card-title {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.lcf-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.lcf-list {
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 13px;
  line-height: 1.5;
  color: #374151;
}

@media (max-width: 1100px) {
  .lcf-header,
  .lcf-toolbar,
  .lcf-usage,
  .lcf-content {
    padding-left: 16px;
    padding-right: 16px;
  }

  .lcf-header {
    flex-direction: column;
    align-items: stretch;
  }

  .lcf-content,
  .lcf-usage__totals,
  .lcf-usage__grid,
  .lcf-columns {
    grid-template-columns: 1fr;
  }
}
</style>
